import _moment, { Moment } from 'moment';
import { effect, untracked } from '@angular/core';
import { FieldTree } from '@angular/forms/signals';

// Valoarea unui datepicker Material legat cu [formField]: '' cand e gol,
// Moment dupa alegere (adaptorul Moment). Un text invalid ajunge ca null.
export type DataCamp = Moment | string | null;

// Autocomplete-urile din registre tin id-ul ales, dar in timpul tastarii
// campul are textul introdus.
export type IdAutocomplete = number | string;

// true cand ambele date sunt alese si data de start e dupa cea de sfarsit
// (aceeasi regula ca validatorul matStartDateInvalid din Material)
export function startDupaSfarsit(start: DataCamp, sfarsit: DataCamp): boolean {
  return !!start && !!sfarsit && _moment(start).isAfter(_moment(sfarsit), 'day');
}

// data pentru API ('YYYY-MM-DD'); null cand campul e gol
export function dataIso(valoare: DataCamp | undefined): string | null {
  if (!valoare) {
    return null;
  }
  return typeof valoare === 'string' ? valoare : valoare.format('YYYY-MM-DD');
}

// '' pentru campurile text care vin null (sau numar) din API: un input text
// cu model null e citit de Signal Forms ca numar
export function text(valoare: unknown): string {
  return valoare == null ? '' : String(valoare);
}

// campurile text venite null din API devin '' (un textarea legat cu [formField] cere text)
export function cuText<T extends object, K extends keyof T>(model: T, campuri: readonly K[]): T {
  const rezultat = { ...model };
  for (const camp of campuri) {
    rezultat[camp] = text(model[camp]) as T[K];
  }
  return rezultat;
}

// la trimitere, textele goale pleaca null, ca valorile neatinse din formularele reactive
export function golNull<T extends object, K extends keyof T>(valori: T, campuri: readonly K[]) {
  const rezultat = { ...valori } as Record<PropertyKey, unknown>;
  for (const camp of campuri) {
    rezultat[camp] = valori[camp] === '' ? null : valori[camp];
  }
  return rezultat as Omit<T, K> & Record<K, T[K] | null>;
}

// La trimitere, un camp text nemodificat pleaca exact ca in datele primite
// (null ramane null, un numar ramane numar), ca la formularele reactive.
export function pastreazaOriginal<T extends object>(valori: T, original: object | null | undefined): T {
  return Object.fromEntries(Object.entries(valori).map(([cheie, valoare]) => {
    const initial = (original as Record<string, unknown> | null | undefined)?.[cheie];
    return [cheie, typeof valoare === 'string' && typeof initial !== 'string' && valoare === text(initial) ? initial ?? null : valoare];
  })) as T;
}

// ca patchValue: ia din sursa doar cheile modelului, pe grupuri imbricate
export function completeaza<T extends object>(model: T, sursa: unknown): T {
  if (!sursa || typeof sursa !== 'object') {
    return model;
  }
  const rezultat = { ...model };
  for (const cheie of Object.keys(model) as (keyof T)[]) {
    const valoare = (sursa as T)[cheie];
    // o cheie nedefinita ar lasa campul fara FieldTree
    if (valoare === undefined) {
      continue;
    }
    const curent = model[cheie];
    rezultat[cheie] = curent && typeof curent === 'object' && !Array.isArray(curent) && !_moment.isMoment(curent)
      ? completeaza(curent, valoare)
      : valoare;
  }
  return rezultat;
}

// ca form.value din formularele reactive: campurile dezactivate nu se trimit
export function faraDezactivate<T extends object>(model: T, campuri: FieldTree<T>): Partial<T> {
  const copii = campuri as unknown as Record<string, FieldTree<unknown>>;
  return Object.fromEntries(Object.entries(model).filter(([cheie]) => !copii[cheie]?.().disabled())) as Partial<T>;
}

// ca un formular reactiv recreat in ngOnChanges: cand se schimba sursele
// (elementul selectat, modul de lucru), campurile revin neatinse
export function neatinsLa<T>(formular: FieldTree<T>, surse: () => unknown) {
  return effect(() => {
    surse();
    untracked(() => formular().reset());
  });
}

// filtrul din autocomplete: textul tastat se cauta literal, fara diferente de majuscule
// (caracterele speciale ca "(" sau "." nu mai sunt interpretate ca expresie regulata)
export function cautareText(cautat: unknown): RegExp {
  return new RegExp(String(cautat).replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
}
